import { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { ContextualPremiumGate } from '@/components/paywall/PremiumGate';
import { useNavigate } from 'react-router-dom';

type PaywallContext = 'insights' | 'family' | 'journey';

interface PremiumRouteProps {
  children: ReactNode;
  context?: PaywallContext;
}

/**
 * Gates a route on confirmed Premium entitlement.
 *
 * Activates only after the webhook (or sync-subscription fallback) has set:
 *   - plan_type in ('premium','family')
 *   - subscription_status === 'active'
 *   - current_period_end > now()
 *
 * Admins and active trial users bypass.
 */
export function PremiumRoute({ children, context = 'insights' }: PremiumRouteProps) {
  const navigate = useNavigate();
  const {
    loading, isPremium, isTrialActive, planType,
    currentPeriodEnd, lsStatus,
  } = useSubscription();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Trial users keep access (isPremium true, no LS row required).
  if (isTrialActive && isPremium) return <>{children}</>;

  // Admins / comped users: isPremium is set but there's no Lemon Squeezy period.
  // Let them through without requiring periodOk.
  if (isPremium && (planType === 'trial' || !currentPeriodEnd)) return <>{children}</>;

  const paid = planType === 'premium' || planType === 'family';
  const periodOk = currentPeriodEnd ? new Date(currentPeriodEnd).getTime() > Date.now() : false;
  const lsOk = !lsStatus || ['active', 'on_trial', 'cancelled', 'past_due'].includes(lsStatus);
  const allowed = isPremium && paid && periodOk && lsOk;

  if (allowed) return <>{children}</>;

  return (
    <div className="min-h-screen bg-background p-4 flex items-center justify-center">
      <div className="w-full max-w-md">
        <ContextualPremiumGate
          context={context}
          onCancel={() => navigate('/dashboard')}
        />
      </div>
    </div>
  );
}
